import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { errorMessage } from "../../api/client";
import type { Player } from "../../api/models";
import { useCreateTask, useTaskActions, type Task } from "../../tasks/api";
import { alertTime, expirationDate, TASK_ALERT_OPTIONS } from "../../tasks/task-logic";
import { PButton } from "../../ui/PButton";
import { PDialog } from "../../ui/PDialog";
import { PDropdown } from "../../ui/PDropdown";
import { PrimeGlyph } from "../../ui/PrimeGlyph";
import { PSidebar } from "../../ui/PSidebar";
import { PInputNumber, PMessage, PTag } from "../../ui/PWidgets";
import { useDebouncedValue } from "../../ui/useDebouncedValue";
import { useToast } from "../../ui/toast";
import { canConfirmOrDecline, eventDate, eventStatusSeverity, roundUpToNextQuarterHour, validateNewTask } from "../profile-logic";
import {
  bookEvent,
  commsPlayerName,
  currentUserId,
  fetchOpenEvents,
  invalidateProfile,
  key as profileKey,
  searchCommsPlayers,
  setBookingStatus,
  submitOverride,
  type CommsPlayer,
  type OpenEvent,
  type PlayerEvent,
} from "../profile-api";
import { PlayerSummary } from "./TaskContactSidebars";

const SIDEBAR_WIDTH = { xs: "100%", md: "30rem" };
// ANGULAR'S `titlecase` PIPE
const titleCase = (s: string | undefined) => (s ?? "").replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
const shortTime = (v: string | undefined) => (v ? dayjs(v).format("h:mm A") : "");

function IconText({ icon, children, className = "" }: { icon: "calendar" | "clock" | "users"; children: React.ReactNode; className?: string }) {
  return (
    <div className={`flex align-items-center text-600 ${className}`}>
      <span className="mr-2" style={{ display: "inline-flex" }}>
        <PrimeGlyph name={icon} />
      </span>
      {children}
    </div>
  );
}

// EVENTS: THE PLAYER'S QUALIFIED EVENTS, NEWEST FIRST - STATUS TAG, NAME, WHEN (ONE DAY WITH A
// TIME RANGE, OR SEPARATE START AND END LINES), BOOKINGS OF CAPACITY, THEN THE BOOKING (WITH
// CONFIRM / DECLINE WHILE IT IS LIVE), A "BOOK EVENT" BUTTON, OR "CANNOT ACCEPT BOOKINGS"
export function EventsSidebar(props: {
  player: Player;
  open: boolean;
  events: PlayerEvent[];
  onClose: () => void;
  onReload: () => void;
  onOfferContact: (note: string) => void;
}) {
  const { player, open, events, onClose, onReload, onOfferContact } = props;
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [booking, setBooking] = useState<PlayerEvent | null>(null);

  const respond = async (e: PlayerEvent, status: "CONFIRMED" | "DECLINED") => {
    if (e.eventBookingId == null) return;
    const title = status === "CONFIRMED" ? "Confirm Event" : "Decline Event";
    setBusy(true);
    try {
      await setBookingStatus(e.eventId, e.eventBookingId, status);
      toast("success", title, `Event ${status === "CONFIRMED" ? "confirmed" : "declined"} for ${player.name}`);
      onReload();
      onOfferContact(`${status === "CONFIRMED" ? "Confirmed" : "Declined"} event: ${e.eventName ?? ""}`);
    } catch (err) {
      toast("error", title, errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PSidebar open={open} onClose={onClose} position="right" width={SIDEBAR_WIDTH} ariaLabel="Qualified Events">
        <div className="parent overflow-hidden">
          <div className="child mb-2 p-3">
            <div className="text-xl font-bold">{titleCase(player.name)}</div>
            <div className="text-md font-bold mt-1">Qualified Events</div>
          </div>
          <div className="child-grow overflow-y-auto">
            {events.length === 0 && <div className="p-3 text-center text-500">No events found for this player</div>}
            {events.map((e) => {
              const sameDay = eventDate(e.startDateTime) === eventDate(e.endDateTime);
              return (
                <div key={`${e.eventId}-${e.eventBookingId ?? ""}`} className="p-3 border-bottom-1 surface-border">
                  <div className="flex justify-content-between align-items-start mb-2">
                    <div className="flex-1">
                      {/* ANGULAR'S `mb-2` SITS ON THE INLINE `p-tag` HOST, WHERE IT HAS NO VERTICAL EFFECT */}
                      <PTag value={e.eventStatus} severity={eventStatusSeverity(e.eventStatus)} />
                      <div className="font-bold text-lg mt-2">{e.eventName}</div>
                    </div>
                    {e.isBooked && (
                      <div className="ml-2">
                        <PTag value={e.eventBookingStatus} severity={eventStatusSeverity(e.eventBookingStatus)} rounded />
                      </div>
                    )}
                  </div>
                  <div className="mt-2">
                    {sameDay ? (
                      <div>
                        <IconText icon="calendar" className="mb-1">
                          <span>{eventDate(e.startDateTime)}</span>
                        </IconText>
                        <IconText icon="clock" className="mb-1">
                          <span>{shortTime(e.startDateTime)}</span>
                          <span className="mx-2">-</span>
                          <span>{shortTime(e.endDateTime)}</span>
                        </IconText>
                      </div>
                    ) : (
                      <div>
                        {[e.startDateTime, e.endDateTime].map((d, i) => (
                          <div key={i} className="flex align-items-center text-600 mb-1">
                            <span className="mr-2" style={{ display: "inline-flex" }}>
                              <PrimeGlyph name="calendar" />
                            </span>
                            <span className="mr-2">{eventDate(d)}</span>
                            <span className="mr-2" style={{ display: "inline-flex" }}>
                              <PrimeGlyph name="clock" />
                            </span>
                            <span>{shortTime(d)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <IconText icon="users" className="mt-2">
                    <span>
                      {e.eventCurrentBookings} / {e.eventTotalCapacity} booked
                    </span>
                  </IconText>

                  {e.isBooked && (
                    <div className="mt-3">
                      <div className="flex justify-content-between align-items-center">
                        <div className="text-sm">
                          <div className="font-bold">Booking Status:</div>
                          <div>{e.eventBookingStatus}</div>
                        </div>
                        {e.eventBookingStatus === "CANCELLED" && (
                          <div className="text-sm">
                            <div className="font-bold">Cancelled At:</div>
                            <div>{e.eventBookingCancelledAt ? dayjs(e.eventBookingCancelledAt).format("M/D/YY, h:mm A") : ""}</div>
                          </div>
                        )}
                      </div>
                      <div className="text-sm mt-2">
                        <div className="font-bold">Tickets:</div>
                        <div>{e.numberOfTickets}</div>
                      </div>
                      {canConfirmOrDecline(e) && (
                        // `styleClass="w-full"` FILLS ONLY THE `<p-button>` HOST, WHICH A FLEX ROW SIZES TO ITS
                        // CONTENT - SO IN ANGULAR THESE STAY AT THEIR NATURAL WIDTH
                        <div className="mt-3 flex gap-2">
                          <PButton label="Confirm" icon="check" hostElement disabled={e.eventBookingStatus === "CONFIRMED" || busy} onClick={() => void respond(e, "CONFIRMED")} />
                          <PButton
                            label="Decline"
                            icon="times"
                            severity="danger"
                            variant="outlined"
                            hostElement
                            disabled={e.eventBookingStatus === "DECLINED" || busy}
                            onClick={() => void respond(e, "DECLINED")}
                          />
                        </div>
                      )}
                    </div>
                  )}
                  {!e.isBooked && e.eventCanAcceptBookings && (
                    <div className="mt-3">
                      <PButton label="Book Event" icon="plus" className="w-full" onClick={() => setBooking(e)} />
                    </div>
                  )}
                  {!e.isBooked && !e.eventCanAcceptBookings && (
                    <div className="mt-3">
                      <PMessage severity="warn" text="Event cannot accept bookings" className="w-full" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </PSidebar>
      <BookingDialog player={player} event={booking} onClose={() => setBooking(null)} onBooked={onReload} />
    </>
  );
}

// "BOOK EVENT": 1-10 TICKETS AND OPTIONAL NOTES, BOOKED AS CONFIRMED FOR THE SIGNED-IN HOST
function BookingDialog({ player, event, onClose, onBooked }: { player: Player; event: PlayerEvent | null; onClose: () => void; onBooked: () => void }) {
  const toast = useToast();
  const [tickets, setTickets] = useState(1);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [seededFor, setSeededFor] = useState<PlayerEvent | null>(null);
  if (event !== seededFor) {
    setSeededFor(event);
    setTickets(1);
    setNotes("");
  }
  const confirm = async () => {
    if (!event) return;
    setSaving(true);
    try {
      await bookEvent({
        eventId: event.eventId,
        playerId: player.id,
        playerName: player.name,
        playerCardNumber: player.cardId,
        hostId: currentUserId(),
        numberOfTickets: tickets,
        notes,
        status: "CONFIRMED",
      });
      toast("success", "Book Event", "Player successfully booked for event");
      onClose();
      onBooked();
    } catch (e) {
      toast("error", "Book Event", errorMessage(e));
    } finally {
      setSaving(false);
    }
  };
  return (
    <PDialog open={event !== null} onClose={onClose} ariaLabel="Book Event" header="Book Event" width={{ xs: "100%", md: "30rem" }}>
      {event && (
        <div>
          <div className="mb-3">
            <div className="font-bold text-lg">{event.eventName}</div>
            <div className="text-sm text-600 mt-1">
              {eventDate(event.startDateTime)} - {shortTime(event.startDateTime)}
            </div>
          </div>
          <div className="mb-3">
            <div className="font-bold">Player:</div>
            <div>
              {player.name} ({player.cardId})
            </div>
          </div>
          <div className="mb-3">
            <label className="font-bold mb-2 block">Number of Tickets:</label>
            <PInputNumber value={tickets} onChange={setTickets} min={1} max={10} ariaLabel="Number of Tickets" />
          </div>
          <div className="mb-3">
            <label className="font-bold mb-2 block" htmlFor="booking-notes">
              Notes (Optional):
            </label>
            <textarea id="booking-notes" className="p-inputtext w-full" rows={3} style={{ resize: "none" }} placeholder="Add any booking notes..." value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div className="flex justify-content-end gap-2">
            <PButton label="Cancel" severity="secondary" variant="outlined" onClick={onClose} />
            <PButton label="Confirm Booking" icon="check" loading={saving} onClick={() => void confirm()} />
          </div>
        </div>
      )}
    </PDialog>
  );
}

// "NEW TASK" FOR THIS PLAYER: THE PLAYER IS FIXED; ACTION, DAY (TODAY ON), START / END (THE NEXT
// QUARTER HOUR, FOR HALF AN HOUR), ALERT, TITLE AND NOTE. ITS MASK DOES NOT CLOSE IT.
export function CreateTaskSidebar({ player, open, onClose }: { player: Player; open: boolean; onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const actions = useTaskActions();
  const create = useCreateTask();
  const blank = () => {
    const s = roundUpToNextQuarterHour(dayjs());
    return { actionId: 0, day: dayjs().format("YYYY-MM-DD"), start: s.format("HH:mm"), end: s.add(30, "minute").format("HH:mm"), alert: "", title: "", note: "" };
  };
  const [d, setD] = useState(blank);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setD(blank());
  }
  const set = (patch: Partial<ReturnType<typeof blank>>) => setD((x) => ({ ...x, ...patch }));

  const save = () => {
    const start = dayjs(`${d.day}T${d.start}`).second(0).millisecond(0).toDate();
    const end = dayjs(`${d.day}T${d.end}`).second(0).millisecond(0).toDate();
    const problem = validateNewTask(start, end);
    if (problem) {
      toast("error", "Create Task", problem);
      return;
    }
    const body = {
      id: null,
      userId: null,
      startDay: dayjs(d.day).startOf("day").toDate(),
      startTime: start,
      endTime: end,
      title: d.title,
      description: d.note,
      managerUserId: null,
      completed: false,
      alertTime: alertTime(d.alert, start),
      alertOption: d.alert,
      expirationDate: expirationDate(end),
      parentTaskId: null,
      taskActionId: d.actionId,
      players: [{ playerId: player.id, taskId: null, playerName: player.name, cardId: player.cardId, completed: false, taskOutcomeId: null, taskOutcomeNote: "" }],
    };
    create.mutate(body as unknown as Task, {
      onSuccess: () => {
        toast("success", "Create Task", "Task was successfully created");
        void invalidateProfile(qc);
        onClose();
      },
      onError: (e) => toast("error", "Create Task", errorMessage(e)),
    });
  };

  const field = "w-full mt-4 flex flex-column";
  return (
    <PSidebar open={open} onClose={onClose} position="right" dismissible={false} width={SIDEBAR_WIDTH} noPadding ariaLabel="New Task">
      <div className="parent overflow-hidden">
        <div className="child mb-2 p-3">
          <div className="text-2xl font-bold">New Task</div>
          <PlayerSummary player={player} />
        </div>
        <div className="child-grow overflow-y-auto p-3">
          <div className="w-full flex flex-column">
            <label className="font-bold mb-1">Action:</label>
            <PDropdown value={d.actionId || ""} options={(actions.data ?? []).map((a) => ({ label: a.name, value: a.id }))} placeholder="Select an action" onChange={(v) => set({ actionId: v })} />
          </div>
          <div className={field}>
            <label className="font-bold mb-1">Day:</label>
            <input type="date" className="p-inputtext w-full" aria-label="Day" min={dayjs().format("YYYY-MM-DD")} value={d.day} onChange={(e) => set({ day: e.target.value })} onClick={openPicker} />
          </div>
          <div className="w-full mt-4 flex align-items-center justify-content-between gap-4">
            <div className="w-full flex flex-column">
              <label className="font-bold mb-1">Start:</label>
              <input type="time" step={900} className="p-inputtext w-full" aria-label="Start" value={d.start} onChange={(e) => set({ start: e.target.value })} onClick={openPicker} />
            </div>
            <div className="w-full flex flex-column">
              <label className="font-bold mb-1">End:</label>
              <input type="time" step={900} className="p-inputtext w-full" aria-label="End" value={d.end} onChange={(e) => set({ end: e.target.value })} onClick={openPicker} />
            </div>
          </div>
          <div className={field}>
            <label className="font-bold mb-1">Alert:</label>
            <div className="p-fluid flex align-items-center">
              <span className="material-symbols-outlined">notifications</span>
              <PDropdown value={d.alert} options={TASK_ALERT_OPTIONS.map((o) => ({ label: o.name, value: o.value }))} ariaLabel="Alert" onChange={(v) => set({ alert: v })} />
            </div>
          </div>
          <div className={field}>
            <label className="font-bold mb-1">Title:</label>
            <input type="text" className="p-inputtext w-full" aria-label="Title" maxLength={50} value={d.title} onChange={(e) => set({ title: e.target.value })} />
          </div>
          <div className={field}>
            <label className="font-bold mb-1">Note:</label>
            <textarea className="p-inputtext w-full" style={{ resize: "none" }} rows={5} maxLength={150} aria-label="Note" value={d.note} onChange={(e) => set({ note: e.target.value })} />
          </div>
          <div className="mt-4 w-full flex align-items-center gap-3">
            <div className="w-full">
              <PButton label="Cancel" className="w-full" onClick={onClose} sx={{ backgroundColor: "var(--orange-600)", color: "var(--surface-0)", "&:hover": { backgroundColor: "var(--orange-600)" } }} />
            </div>
            <div className="w-full">
              <PButton label="Save" className="w-full" hostElement loading={create.isPending} disabled={!d.title || create.isPending} onClick={save} />
            </div>
          </div>
        </div>
      </div>
    </PSidebar>
  );
}

function openPicker(e: React.MouseEvent<HTMLInputElement>) {
  try {
    e.currentTarget.showPicker();
  } catch {
    // `showPicker` THROWS WHEN THE BROWSER REFUSES; TYPING STILL WORKS
  }
}

// REQUEST OVERRIDE (`override-request`): ASK A MANAGER TO LET A NON-QUALIFIED PLAYER BOOK AN
// OPEN EVENT. OPENS PRE-FILLED WITH THIS PLAYER; EITHER PLAYER FIELD SEARCHES (2+ CHARACTERS)
// AND PICKING A SUGGESTION FILLS BOTH, WHILE EDITING AWAY FROM IT DROPS THE LINK SO A STALE
// PLAYER ID IS NEVER SUBMITTED.
export function OverrideSidebar({ player, open, onClose }: { player: Player; open: boolean; onClose: () => void }) {
  const toast = useToast();
  // RE-READ EACH TIME THE SIDEBAR OPENS (IT IS STALE AT ONCE), AS ANGULAR RELOADS ON EVERY `open()`
  const events = useQuery({ queryKey: profileKey("override-open-events"), queryFn: fetchOpenEvents, enabled: open, staleTime: 0 });
  const [search, setSearch] = useState("");
  const [eventId, setEventId] = useState<number | "">("");
  const [card, setCard] = useState("");
  const [name, setName] = useState("");
  const [selected, setSelected] = useState<{ playerId?: number; cardId?: string; fullName: string } | null>(null);
  const [tickets, setTickets] = useState(1);
  const [justification, setJustification] = useState("");
  const [query, setQuery] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [wasOpen, setWasOpen] = useState(false);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setSearch("");
      setEventId("");
      setCard(player.cardId ?? "");
      setName(player.name ?? "");
      setSelected({ cardId: player.cardId, playerId: player.id, fullName: player.name ?? "" });
      setTickets(1);
      setJustification("");
      setQuery("");
    }
  }
  // ONE TOAST PER FAILED LOAD
  useEffect(() => {
    if (events.isError) toast("error", "Error", "Failed to load booking events");
  }, [events.isError, events.errorUpdatedAt, toast]);

  const debounced = useDebouncedValue(query, 300);
  const suggestions = useQuery({
    queryKey: profileKey("override-player-search", debounced),
    queryFn: ({ signal }) => searchCommsPlayers(debounced, signal),
    enabled: debounced.trim().length >= 2,
  });
  const options = debounced.trim().length >= 2 ? (suggestions.data ?? []) : [];

  const filtered = (events.data ?? []).filter((e) => !search || e.name.toLowerCase().includes(search.toLowerCase()));
  const pick = (p: CommsPlayer) => {
    const fullName = commsPlayerName(p);
    setSelected({ playerId: p.playerId, cardId: p.cardId, fullName });
    setCard(p.cardId ?? "");
    setName(fullName);
  };

  const submit = async () => {
    if (!eventId || !card || !justification) {
      toast("warning", "Validation Error", "Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await submitOverride({
        eventId,
        playerId: selected?.playerId || 0,
        playerCardNumber: card,
        playerName: name,
        userId: currentUserId(),
        numberOfTickets: tickets,
        justification,
        status: "PENDING",
      });
      toast("success", "Success", "Override request submitted successfully");
      onClose();
    } catch {
      toast("error", "Error", "Failed to submit override request");
    } finally {
      setSubmitting(false);
    }
  };

  const playerField = (id: string, label: string, value: string, placeholder: string, onText: (v: string) => void, matches: (s: string) => boolean) => (
    <div className="field mb-4">
      <label htmlFor={id}>{label}</label>
      <Autocomplete<CommsPlayer, false, true, true>
        freeSolo
        disableClearable
        options={options}
        filterOptions={(x) => x}
        inputValue={value}
        onInputChange={(_, v, reason) => {
          if (reason === "reset") return;
          onText(v);
          setQuery(v);
          if (selected && !matches(v)) setSelected(null);
        }}
        onChange={(_, v) => {
          if (v && typeof v === "object") pick(v);
        }}
        getOptionLabel={(o) => (typeof o === "string" ? o : commsPlayerName(o))}
        noOptionsText={suggestions.isFetching ? "Searching..." : "No players found"}
        renderOption={(liProps, o) => {
          const { key, ...rest } = liProps as typeof liProps & { key: string };
          return (
            <li key={key} {...rest}>
              <div className="flex flex-column">
                <span className="font-medium">{commsPlayerName(o)}</span>
                <span className="text-sm text-500">{o.cardId}</span>
              </div>
            </li>
          );
        }}
        // `p-autoComplete` WITH `[showClear]`: A 30PX `p-inputtext` FIELD AND A GRAY "X" THAT EMPTIES IT
        renderInput={(params) => (
          <TextField
            {...params}
            id={id}
            placeholder={placeholder}
            slotProps={{
              ...params.slotProps,
              input: {
                ...params.slotProps.input,
                endAdornment: value ? (
                  <button
                    type="button"
                    aria-label={`Clear ${label.replace(/ *| (Optional)/g, "")}`}
                    onClick={() => {
                      onText("");
                      setQuery("");
                      setSelected(null);
                    }}
                    style={{ position: "absolute", right: "0.75rem", border: 0, background: "none", padding: 0, color: "#6c757d", cursor: "pointer", display: "flex" }}
                  >
                    <PrimeGlyph name="times" />
                  </button>
                ) : undefined,
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": { padding: "0 !important" },
              "& .MuiOutlinedInput-root .MuiAutocomplete-input": { padding: "calc(0.5rem + 1px) 2.5rem calc(0.5rem + 1px) calc(0.75rem + 1px) !important", height: "14px" },
            }}
          />
        )}
      />
    </div>
  );

  return (
    <PSidebar
      open={open}
      onClose={onClose}
      position="right"
      width={SIDEBAR_WIDTH}
      ariaLabel="Request Override"
      header={<h3 className="m-0">Request Override</h3>}
      footer={
        <div className="flex gap-2 justify-content-end">
          <PButton label="Cancel" icon="times" severity="secondary" variant="outlined" onClick={onClose} />
          <PButton label="Submit Request" icon="check" hostElement disabled={!eventId || !card || !justification || events.isPending || submitting} loading={submitting} onClick={() => void submit()} />
        </div>
      }
    >
      <div className="p-fluid">
        <div className="field mb-4">
          <label htmlFor="override-search">Search Event</label>
          <div className="w-full" style={{ position: "relative" }}>
            <span style={{ position: "absolute", left: "0.75rem", top: "50%", marginTop: "-0.5rem", color: "#495057", display: "flex" }}>
              <PrimeGlyph name="search" />
            </span>
            <input id="override-search" type="text" className="p-inputtext w-full" style={{ paddingLeft: "2.5rem" }} placeholder="Search open events..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
        <div className="field mb-4">
          <label htmlFor="override-event">Select Event *</label>
          <OpenEventDropdown events={filtered} value={eventId} onChange={setEventId} />
          <small className="text-600">Only open events are available</small>
        </div>
        {playerField("override-card", "Player Card Number *", card, "Enter player card number", setCard, (v) => v === (selected?.cardId ?? ""))}
        {playerField("override-name", "Player Name (Optional)", name, "Enter player name", setName, (v) => v === (selected?.fullName ?? ""))}
        <div className="field mb-4">
          <label>Number of Tickets *</label>
          <PInputNumber value={tickets} onChange={setTickets} min={1} max={10} ariaLabel="Number of Tickets" />
        </div>
        <div className="field mb-4">
          <label htmlFor="override-justification">Justification *</label>
          <textarea
            id="override-justification"
            className="p-inputtext w-full"
            rows={5}
            placeholder="Provide justification for why this player should be added to the qualified list..."
            value={justification}
            onChange={(e) => setJustification(e.target.value)}
          />
          <small className="text-600">Explain why this non-qualified player should receive tickets</small>
        </div>
        <PMessage severity="info" text="This player will be marked as NON-QUALIFIED in the qualified players list once approved by a manager." className="mb-3" />
      </div>
    </PSidebar>
  );
}

// "SELECT AN EVENT": EACH OPTION IS THE EVENT NAME OVER ITS FULL DATE; CLEARABLE
function OpenEventDropdown({ events, value, onChange }: { events: OpenEvent[]; value: number | ""; onChange: (v: number | "") => void }) {
  return (
    <div className="flex align-items-center gap-2">
      <PDropdown
        value={value}
        options={events.map((e) => ({ label: `${e.name}${e.startDateTime ? ` - ${dayjs(e.startDateTime).format("dddd, MMMM D, YYYY")}` : ""}`, value: e.id }))}
        placeholder="Select an event"
        ariaLabel="Select Event"
        onChange={onChange}
      />
      {value !== "" && <PButton icon="times" rounded variant="text" severity="secondary" ariaLabel="Clear event" onClick={() => onChange("")} />}
    </div>
  );
}
